import { ArrowLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { Formik, Form } from "formik";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@volca/ui/components/ui/input-otp";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useBreakpoints } from "@volca/ui/hooks/use-breakpoints";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { getErrorMessage } from "@/core/api/errors";
import {
  isInvalidPasswordResetCodeError,
  isPasswordResetThrottledError,
  useAuth,
} from "@/core/auth";
import { ResendCodeButton } from "./resend-code-button";

const resetPasswordCodeSchema = Yup.object().shape({
  verification_code: Yup.string().required("Code is required"),
});

const CODE_ERROR_ID = "reset-code-error";

export default function ResetPasswordCode() {
  const {
    resetPasswordCode,
    forgotPassword,
    forgotPasswordEmail: email,
  } = useAuth();
  const navigate = useNavigate();
  const { isSmallMobile } = useBreakpoints();

  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(true);
  const [codeError, setCodeError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (values: { verification_code: string }) => {
      try {
        setAutoSubmitEnabled(false);
        await resetPasswordCode({
          ...values,
          email: email || "",
        });
      } catch (error) {
        if (isInvalidPasswordResetCodeError(error)) {
          setCodeError(
            "That code didn't work. Check your latest email, or resend the code.",
          );
        } else if (isPasswordResetThrottledError(error)) {
          setCodeError(error.message);
        } else if (error instanceof Error) {
          toast.error("Couldn't verify the code", {
            description: getErrorMessage(error),
          });
        } else {
          toast.error(
            "Couldn't verify the code. Please try again, or contact support.",
          );
        }
      }
    },
    [email, resetPasswordCode],
  );

  const handleResendCode = useCallback(
    async (clearCode: () => void) => {
      if (!email) {
        toast.error("No email found. Please start again.");
        return navigate("/forgot-password");
      }

      try {
        await forgotPassword({ email });
        clearCode();
        setCodeError(null);
        setAutoSubmitEnabled(true);
        toast.success("New code sent", {
          description: `Check ${email}. Earlier codes no longer work.`,
        });
      } catch (error) {
        toast.error("Couldn't resend the code", {
          description: getErrorMessage(error),
        });
      }
    },
    [email, forgotPassword, navigate],
  );

  return (
    <Stack
      gap={4}
      direction="row"
      align="center"
      justify="center"
      className="min-h-full w-full overflow-hidden p-6"
    >
      <Card className="w-full max-w-md gap-0 p-6 pt-8">
        <Formik
          initialValues={{ verification_code: "" }}
          validationSchema={resetPasswordCodeSchema}
          validateOnMount
          onSubmit={async (values, { setSubmitting }) =>
            handleSubmit(values).finally(() => {
              setSubmitting(false);
            })
          }
        >
          {({ values, isSubmitting, isValid, setFieldValue }) => (
            <Form>
              <Stack className="w-full" align="start" gap={6}>
                <Text variant="title" render={<h1 />}>
                  Enter reset code
                </Text>

                <Text tone="muted" className="w-full wrap-break-word">
                  {email
                    ? `We sent a 6-digit code to ${email}. Enter it below to continue.`
                    : "Check your email for the 6-digit reset code and enter it below."}
                </Text>

                <Stack className="w-full" align="start" gap={1}>
                  <InputOTP
                    maxLength={6}
                    autoFocus
                    aria-label="Reset code"
                    aria-invalid={codeError ? true : undefined}
                    aria-describedby={CODE_ERROR_ID}
                    value={values.verification_code}
                    onChange={(value) => {
                      setCodeError(null);
                      void setFieldValue("verification_code", value);
                      if (autoSubmitEnabled && value.length === 6) {
                        void handleSubmit({
                          ...values,
                          verification_code: value,
                        });
                      }
                    }}
                    containerClassName={cn("justify-center mx-auto", {
                      "gap-1": isSmallMobile,
                      "gap-3": !isSmallMobile,
                    })}
                  >
                    {Array.from({ length: 6 }).map((_, index) => (
                      <InputOTPGroup key={index}>
                        <InputOTPSlot
                          index={index}
                          className={cn(
                            "border-input-line bg-input text-center text-sm shadow-xs data-[active=true]:border-focus",
                            {
                              "border-danger-line": codeError,
                              "h-10 w-8": isSmallMobile,
                              "h-12 w-12": !isSmallMobile,
                            },
                          )}
                        />
                      </InputOTPGroup>
                    ))}
                  </InputOTP>

                  <Text
                    id={CODE_ERROR_ID}
                    aria-live="polite"
                    tone="destructive"
                    className="text-center text-pretty"
                  >
                    {codeError}
                  </Text>
                </Stack>

                <ResendCodeButton
                  onResend={() =>
                    handleResendCode(() =>
                      setFieldValue("verification_code", ""),
                    )
                  }
                />

                <Stack className="w-full" align="start" gap={2}>
                  <Button
                    type="submit"
                    size="lg"
                    disabled={!isValid || isSubmitting}
                    className="w-full"
                    loading={isSubmitting}
                  >
                    Continue
                  </Button>

                  <Button
                    type="button"
                    size="lg"
                    variant="ghost"
                    onClick={() => navigate("/forgot-password")}
                    className="w-full"
                  >
                    <ArrowLeftIcon data-icon="inline-start" />
                    Back to reset password
                  </Button>
                </Stack>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card>
    </Stack>
  );
}
